import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useRoutesProvider } from '../../../providers/provider-hooks';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import PageWrap from '../../../components/auth/page-wrap';
import { getErrorMessage, getErrorStatus, isApiValidationError } from '../../../core/api/response-handler';
import { trackOtpResent, trackOtpVerifyFailed, type FluxCod } from '@/telemetry/posthog-events';
import { cereCodOtp, inregistreazaCodOtp } from '../api/otp.api';
import { useOtpVerify } from '../hooks/useOtpVerify';
import { AUTH_PAROLA_ACTIVA } from '../utils/parola-flag';
import type { DateInregistrareCod } from '../utils/inregistrare-cod';
import {
    LUNGIME_COD,
    casuteGoale,
    codScris,
    esteComplet,
    formateazaSecunde,
    scrieInCaseta,
    seteazaCifra,
    stareRetrimitere
} from '../utils/otp-cod';
import { ETICHETA_COD_NOU, mesajEsecVerificare } from '../utils/otp-mesaje';
import { pastreazaStareCod, restaureazaStareCod, stergeStareCod } from '../utils/otp-stare-pastrata';

/**
 * Ecranul 2 din autentificarea cu cod (U2), pe macheta `b-autentificare-cod.html`.
 *
 * 🔴 Eroarea spune MOTIVUL, cand il spune serverul (din 2026-10-08). `/verify` raspunde tot cu
 * 400, dar cu `motiv`: `COD_EXPIRAT` si `COD_FOLOSIT` doar pentru un cod chiar emis pentru contul
 * acela, `COD_INVALID` pentru orice altceva. Textul il alege `mesajEsecVerificare` (pur, testat);
 * ecranul nu deduce nimic dintr-un ceas propriu. Pana atunci era un singur text — „nu se
 * potrivește sau a expirat" — pentru ca serverul avea un singur raspuns.
 *
 * 🔴 ORICE cod primit si inca valabil e acceptat, nu doar ultimul: omul care a cerut doua coduri
 * il scrie pe primul care ii ajunge, si trebuie sa mearga. Niciun text de aici nu cere „cel mai
 * recent cod".
 *
 * 🔴 Ecranul SE REFACE dupa o reincarcare (`otp-stare-pastrata.ts`): identificatorul, fluxul si
 * clipa cererii stau in `sessionStorage` cat e valabil codul. Fara asta, omul care iese sa
 * citeasca emailul si se intoarce intr-un WebView reincarcat ateriza pe „Nu știm pentru ce cont
 * e codul" si cerea inca unul. Se citeste DOAR cand ruta n-a adus `state`.
 *
 * 🔴 Ecranul e AGNOSTIC DE CANAL: nu numeste nicaieri pe unde pleaca codul.
 * Singurul care o spune e mesajul serverului, randat ca atare — azi zice un lucru,
 * maine poate zice altul, si frontendul nu se atinge. Ancora e EXACT ce a scris
 * omul (telefon sau email), cu „Schimbă" alaturi: nu e o dezvaluire, e propria lui
 * introducere, si trebuie s-o poata vedea fara sa se intoarca.
 *
 * La capat, dupa T1: SESIUNE. Tokenurile se stocheaza si redirectul se face prin
 * `useOtpVerify`, pe aceeasi cale ca login-ul, cu acelasi resolver — deci un profesor
 * care trebuie sa confirme participarea ajunge pe confirmare, nu in home. Ecranul asta
 * nu decide unde se aterizeaza, si nu trebuie sa inceapa s-o faca.
 */

export default function VerificareCodPage() {
    const { PATHS, PAGES } = useRoutesProvider();
    const location = useLocation();
    const stare = (location.state as {
        identificator?: string; cerutLa?: number; mesaj?: string; origine?: 'autentificare'; flux?: 'inregistrare';
        dateInregistrare?: DateInregistrareCod; codNouDupa?: number;
    } | null) ?? null;
    // Dupa o reincarcare ruta nu mai aduce `state`: se reface din ce s-a pastrat, daca mai e
    // valabil. `useState` cu initializator: se citeste O DATA, la montare, nu la fiecare randare.
    const [pastrata] = React.useState(() => (stare?.identificator ? null : restaureazaStareCod(Date.now())));
    const flux: FluxCod = (stare?.flux ?? pastrata?.flux) === 'inregistrare' ? 'inregistrare' : 'autentificare';
    // De unde a venit codul: «Schimbă» se intoarce pe acel pas (`/autentificare/email` sau
    // `/cod-de-acces`). Vezi `stareVerificareCod`.
    const origine = stare?.origine ?? pastrata?.origine;

    // Ancora e EXACT ce a scris omul — telefon sau email. Nu sunt date de cont, deci
    // nu se mascheaza si nu se dezvaluie nimic: e propria lui introducere.
    const identificator = stare?.identificator ?? pastrata?.identificator ?? '';
    // Mesajul serverului, singurul loc care poate spune pe unde a plecat codul.
    // Cand lipseste (intrare directa pe ruta, ecran refacut), se spune conditionalul neutru.
    const mesajServer = (stare?.mesaj ?? '').trim();
    const [cerutLa, setCerutLa] = React.useState<number | null>(stare?.cerutLa ?? pastrata?.cerutLa ?? null);
    // Clipa pana la care serverul nu mai trimite coduri (429 `LIMITA_COD` la inscriere).
    const [codNouDupa, setCodNouDupa] = React.useState<number | null>(stare?.codNouDupa ?? pastrata?.codNouDupa ?? null);
    const [mesajNou, setMesajNou] = React.useState<string>('');
    const [casute, setCasute] = React.useState<string[]>(casuteGoale());
    const [acum, setAcum] = React.useState(() => Date.now());
    // Starea „se verifică" vine din mutație, nu dintr-un al doilea `useState` ținut în
    // paralel cu ea: două surse pentru aceeași stare diverg exact la eșecuri.
    const { verifica, seVerifica } = useOtpVerify(flux);
    const [eroare, setEroare] = React.useState<string | null>(null);
    // Cate verificari au picat la rand: de la a doua, mesajul sugereaza un cod nou. E si cheia
    // alertei — un text identic pe un element ramas montat nu se mai anunta a doua oara.
    const [esecuri, setEsecuri] = React.useState(0);
    // 🔴 Garda de dubla trimitere (verificare SI recerere, una odata) e un REF, nu `seVerifica`: `isPending` se schimba abia la
    //    randarea urmatoare, deci doua atingeri (sau atingere + Enter) din acelasi cadru treceau
    //    amandoua de `if (seVerifica)`. A doua primea 400 pe un cod tocmai consumat de prima —
    //    adica o eroare afisata peste o intrare reusita.
    const inZbor = React.useRef(false);

    const refuri = React.useRef<Array<HTMLInputElement | null>>([]);

    // Ceasul numaratorii. Un interval de o secunda e destul: numaratoarea se scrie
    // in m:ss, deci nimic sub secunda nu se vede.
    React.useEffect(() => {
        const t = window.setInterval(() => setAcum(Date.now()), 1000);
        return () => window.clearInterval(t);
    }, []);

    // Ce trebuie ca ecranul sa se poata reface dupa o reincarcare. Se rescrie la fiecare cerere
    // noua de cod (`cerutLa` se schimba), ca valabilitatea sa porneasca de la ultimul cod.
    React.useEffect(() => {
        if (!identificator || cerutLa === null) return;
        pastreazaStareCod({ identificator, flux, cerutLa, origine, codNouDupa: codNouDupa ?? undefined });
    }, [identificator, flux, cerutLa, origine, codNouDupa]);

    const retrimitere = stareRetrimitere(cerutLa, acum, codNouDupa);
    const complet = esteComplet(casute);

    // Regulile (cifra peste cifra, lipire, cod intreg) sunt in `scrieInCaseta`, pur si testat.
    function scrieCifra(index: number, valoare: string, eLipire = false) {
        const { casute: urmatoare, focus } = scrieInCaseta(casute, index, valoare, eLipire);
        setCasute(urmatoare);
        refuri.current[focus]?.focus();
    }

    function laTasta(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Backspace' && !casute[index] && index > 0) {
            // Stergerea intr-o caseta goala sare inapoi: altfel omul ramane blocat
            // pe o caseta care nu are ce sterge.
            e.preventDefault();
            setCasute(seteazaCifra(casute, index - 1, ''));
            refuri.current[index - 1]?.focus();
        }
    }

    async function continua() {
        if (!complet || inZbor.current) return;
        inZbor.current = true;
        setEroare(null);
        try {
            // Sesiunea si navigarea sunt in `useOtpVerify`, pe aceeasi cale ca login-ul.
            // Aici ramane DOAR eroarea, ca sa nu existe doua locuri care decid ce vede omul.
            await verifica({ identificator, cod: codScris(casute) });
            // Codul e consumat: ecranul asta nu mai are ce reface.
            stergeStareCod();
        } catch (err) {
            trackOtpVerifyFailed(flux, getErrorStatus(err));
            if (isApiValidationError(err) && err.status === 400) {
                // Un 400 de la `/verify` vine cu `motiv`; textul e al nostru, ales dupa el.
                // Sirul serverului („Cod invalid sau expirat.") nu se repeta: „invalid" e
                // cuvant de sistem.
                const urmatorul = esecuri + 1;
                setEsecuri(urmatorul);
                setEroare(mesajEsecVerificare(err.motiv, urmatorul).text);
            } else {
                setEroare(getErrorMessage(err, 'Codul nu a putut fi verificat. Încearcă din nou.'));
            }
        } finally {
            inZbor.current = false;
        }
    }

    async function retrimite() {
        // Aceeasi garda pe ref ca la verificare: o dubla atingere ar cere DOUA coduri, iar
        // numarul de coduri per cont e plafonat de server.
        if (!retrimitere.poateCere || !identificator || inZbor.current) return;
        inZbor.current = true;
        setEroare(null);
        try {
            // La inscriere, resend-ul trebuie sa refaca EXACT cererea initiala
            // (`inregistreazaCodOtp`), ca sa duca mai departe si telefonul din formular -- vezi
            // N1 in OtpService: `/otp/request` genereaza un cod fara `telefon_solicitat`.
            // ⚠️ Pe un ecran REFACUT dupa reincarcare formularul nu mai exista (nu se pastreaza
            // deliberat — nume, telefon), deci recererea cade pe `/otp/request`.
            setMesajNou(flux === 'inregistrare' && stare?.dateInregistrare
                ? await inregistreazaCodOtp(stare.dateInregistrare)
                : await cereCodOtp(identificator));
            trackOtpResent(flux);
            setCerutLa(Date.now());
            setCodNouDupa(null);
            setEsecuri(0);
            setCasute(casuteGoale());
            refuri.current[0]?.focus();
        } catch (err) {
            setEroare(getErrorMessage(err, 'Nu am putut cere un cod nou. Încearcă din nou.'));
        } finally {
            inZbor.current = false;
        }
    }

    // Fara identificator nu se poate verifica nimic: se intra pe ecranul asta doar venind
    // de la cererea codului. Un mesaj cinstit, si calea inapoi.
    if (!identificator) {
        return (
            <PageWrap>
                <Card className="mx-auto mb-4 w-full max-w-md">
                    <CardHeader>
                        <CardTitle as="h1" className="font-heading">Introdu codul de verificare</CardTitle>
                        <CardDescription>
                            Nu știm pentru ce cont e codul. Cere-l din nou și îl verificăm imediat.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Link
                            to={PATHS[PAGES.CERERE_COD]}
                            className="flex min-h-11 w-full items-center justify-center rounded-md border border-input bg-white px-4 font-semibold text-dark"
                        >
                            Cere un cod
                        </Link>
                    </CardContent>
                </Card>
            </PageWrap>
        );
    }

    return (
        <PageWrap>
            <Card className="mb-4 w-full max-w-md">
                <CardHeader>
                    <CardTitle as="h1" className="font-heading">Introdu codul de verificare</CardTitle>
                    <CardDescription>
                        {mesajNou || mesajServer || 'Dacă adresa are un cont, primești un cod de verificare.'}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {/* Destinatia ramane pe ecran si se poate schimba: oamenii nu-si
                        amintesc cu ce s-au inscris, iar un text static i-ar informa si
                        i-ar lasa blocati. */}
                    <p className="mb-4 text-sm text-gray-600 m-0">
                        Pentru <strong className="break-all text-dark">{identificator}</strong>{' '}
                        <Link
                            to={PATHS[origine === 'autentificare' ? PAGES.LOGIN_EMAIL : PAGES.CERERE_COD]}
                            state={{ identificator, email: identificator }}
                            // Omul schimba adresa: ce s-a pastrat pentru cea veche nu mai e de refacut.
                            onClick={() => stergeStareCod()}
                            className="py-3.5 font-semibold text-magenta underline"
                        >
                            Schimbă
                        </Link>
                    </p>

                    {/* `form` + buton `submit`: Enter (sau «Go» de pe tastatura numerica) trimite
                        codul, pe aceeasi cale ca atingerea butonului — si prin aceeasi garda. */}
                    <form
                        noValidate
                        onSubmit={(e) => {
                            e.preventDefault();
                            void continua();
                        }}
                    >
                    <p className="form-label-bold mb-1.5">Codul din 6 cifre</p>
                    {/* `gap-1`, nu `gap-2`: la 360px, 6 casete la min 44px + 5 goluri de
                        8px nu incap in latimea cardului -- singura tinta sub 44px din tot
                        fluxul A (constatarea #32, S1). Cu goluri de 4px incap. */}
                    <div className="flex gap-1" role="group" aria-label="Codul din 6 cifre">
                        {casute.map((cifra, i) => (
                            <input
                                key={i}
                                ref={(el) => { refuri.current[i] = el; }}
                                // `type="text"`, niciodata `number`: codul e un sir care poate
                                // incepe cu 0, nu o cantitate (sageti, zerouri taiate, rotita).
                                type="text"
                                value={cifra}
                                onChange={(e) => scrieCifra(i, e.target.value)}
                                onKeyDown={(e) => laTasta(i, e)}
                                onPaste={(e) => {
                                    e.preventDefault();
                                    scrieCifra(i, e.clipboardData.getData('text'), true);
                                }}
                                // `one-time-code` + tastatura numerica: pe Android codul se
                                // completeaza singur, iar Android e ~6:1 fata de iOS aici.
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                aria-label={'Cifra ' + (i + 1) + ' din ' + LUNGIME_COD}
                                aria-invalid={eroare ? true : undefined}
                                aria-describedby={eroare ? 'otp-cod-eroare' : undefined}
                                // `LUNGIME_COD`, nu 1: autocompletarea si o cifra tastata peste
                                // una existenta livreaza mai multe caractere; le asaza `scrieInCaseta`.
                                maxLength={LUNGIME_COD}
                                className={
                                    'h-14 min-w-11 flex-1 rounded-lg border bg-white text-center font-heading text-2xl font-bold text-dark ' +
                                    (eroare ? 'border-error' : 'border-input')
                                }
                            />
                        ))}
                    </div>

                    {/* Eroarea sta SUB casete, langa ce trebuie corectat: la 360, un
                        banner sus ajunge departe de campul care l-a produs. `role="alert"` o
                        anunta, `aria-describedby` de pe casete o leaga de ele, iar `key` o
                        remonteaza la fiecare esec, ca un text repetat sa fie anuntat din nou. */}
                    {eroare && (
                        <p key={esecuri} id="otp-cod-eroare" role="alert" data-eroare-otp className="mt-3 rounded-lg border border-error bg-error/10 p-3 text-small text-error m-0">
                            {eroare}
                        </p>
                    )}

                    <div className="mt-4 flex items-center justify-between gap-2.5 text-sm text-gray-600">
                        {retrimitere.poateCere ? (
                            <span>Nu ai primit codul?</span>
                        ) : (
                            <span className="tabular-nums">
                                Poți cere alt cod în {formateazaSecunde(retrimitere.secundeRamase)}
                            </span>
                        )}
                        <button
                            type="button"
                            onClick={retrimite}
                            aria-disabled={!retrimitere.poateCere}
                            className={
                                'min-h-11 font-semibold underline ' +
                                (retrimitere.poateCere ? 'text-magenta' : 'cursor-not-allowed text-gray-600')
                            }
                        >
                            {ETICHETA_COD_NOU}
                        </button>
                    </div>

                    <Button
                        type="submit"
                        size="lg"
                        aria-disabled={!complet || seVerifica}
                        className={'mt-4 min-h-11 w-full ' + (complet ? '' : 'cursor-not-allowed bg-light text-dark hover:bg-light')}
                    >
                        {/* „Se autentifică…" e sirul care exista deja pe butonul principal din
                            `LoginPage` si pe cel Google — aceeasi actiune, acelasi cuvant.
                            Un sir nou ar fi insemnat inca un mod de a numi acelasi lucru. */}
                        {seVerifica ? 'Se autentifică...' : 'Intră în cont'}
                    </Button>
                    </form>
                    {/* Iesirea onesta sta MEREU pe ecran, nu dupa o eroare: elevii si
                        conturile fara telefon nu primesc niciodata cod, iar serverul nu
                        dezvaluie cine sunt. Ecranul nu spune „tu esti in categoria aia" —
                        ofera drumul, fara sa-l explice.
                        ⚠️ `py-3.5` e aria de atingere, nu decor: linkul masura 39px
                        inaltime, sub pragul de 44. Ramane INLINE, o propozitie —
                        padding-ul unui element inline intra in caseta lui si in
                        zona de clic, fara sa mute textul cu un pixel. Un buton plin
                        ar concura cu «Continuă» si ar impinge omul spre iesire
                        tocmai cand vrem sa mai incerce. */}
                    {/* `mt-6`, nu `mt-2`: randul de sub buton a disparut (T1), deci iesirea
                        a urcat langa «Intră în cont». Linkul are 14px de padding in sus —
                        padding pe inline creste caseta de clic fara sa creasca inaltimea
                        liniei — deci la 8px distanta cele doua tinte impart 6px, si castiga
                        linkul. 24 = 14 + 8 rotunjit la scara de spacing. Masurat: poarta 14
                        a picat exact pe perechea asta inainte de schimbare. */}
                    {/* Acelasi motiv ca pe `CerereCodPage`: fara parola, ruta nu exista si textul
                        ar contrazice ecranul de logare. «Retrimite» de mai sus ramane iesirea. */}
                    {AUTH_PAROLA_ACTIVA && (
                    <p className="mt-6 text-center text-sm m-0">
                        Nu primești codul?{' '}
                        <Link to={PATHS[PAGES.FORGOTTEN_PASSWORD]} className="py-3.5 font-semibold text-magenta underline">
                            Poți intra resetând parola prin email
                        </Link>
                    </p>
                    )}
                </CardContent>
            </Card>
        </PageWrap>
    );
}
